// MARK://dhk8/admin-shell/lib/clients/modifiers
// Typed client for dhk8-catalog-modifiers — mirrors the published contract at
// services/dhk8-catalog-modifiers/src/contract.ts. Bug #96: ported from
// apps/dhk8-pos/src/lib/clients/modifiers.ts and extended with the full CRUD
// surface (groups + options + product link) the admin page needs.
//
// Routes: GET/POST/PATCH/DELETE /v1/modifier-groups[/:id[/options[/:optionId]|/products/:productId/link]]
// Envelope: { ok, data } | bare T. The service emits the envelope on writes and
// bare lists on reads — we accept both.
//
// PREVIEW_STUB short-circuits to an in-memory store so the admin works without
// the backend (same pattern as ../catalog.ts).

import { PREVIEW_STUB, mock } from '../env.js'
import { authTenantId } from '../../stores/auth-store.js'
import { gqlClient } from '../gql.js'
import { AdminModifierGroupsQuery, AdminCreateModifierGroupMutation, AdminUpdateModifierGroupMutation, AdminDeleteModifierGroupMutation, AdminAddModifierOptionMutation, AdminUpdateModifierOptionMutation, AdminRemoveModifierOptionMutation, AdminLinkModifierGroupToProductMutation, AdminUnlinkModifierGroupFromProductMutation } from '../queries/modifiers.queries.js'


// ── DTOs (mirror of services/dhk8-catalog-modifiers/src/contract.ts) ────────────

export interface ModifierOptionDto {
  id: string
  groupId: string
  label: string
  priceDeltaCents: number
  nestedGroupId: string | null
  active: boolean
  sortOrder: number
}

export interface ModifierGroupDto {
  id: string
  tenantId: string
  name: string
  minSelect: number
  maxSelect: number
  isRequired: boolean
  active: boolean
  createdAt: string
  updatedAt: string
  deletedAt: string | null
  options: ModifierOptionDto[]
  /** Optional product link ids — backend returns when expand=products requested.
   *  Not currently provided by the list endpoint, so the page resolves these
   *  lazily on edit. */
  productIds?: string[]
}

export interface ModifierGroupListResponse {
  items: ModifierGroupDto[]
  total: number
}

export interface ListModifierGroupsParams {
  tenantId?: string
  productId?: string
  page?: number
  limit?: number
}

export interface CreateGroupInput {
  name: string
  minSelect?: number
  maxSelect?: number
  isRequired?: boolean
}

export interface UpdateGroupInput {
  name?: string
  minSelect?: number
  maxSelect?: number
  isRequired?: boolean
  active?: boolean
}

export interface CreateOptionInput {
  label: string
  priceDeltaCents?: number
  sortOrder?: number
}

export interface UpdateOptionInput {
  label?: string
  priceDeltaCents?: number
  sortOrder?: number
  active?: boolean
}

// ── PREVIEW STUB in-memory store ───────────────────────────────────────────
// Keeps the page interactive without the backend (`VITE_API_STUB=1`).
const stubGroups: ModifierGroupDto[] = [
  {
    id: 'mg-extras',
    tenantId: 'tnt_demo',
    name: 'Extras',
    minSelect: 0,
    maxSelect: 3,
    isRequired: false,
    active: true,
    createdAt: new Date('2026-01-01').toISOString(),
    updatedAt: new Date('2026-01-01').toISOString(),
    deletedAt: null,
    options: [
      { id: 'mo-x1', groupId: 'mg-extras', label: 'Queso extra', priceDeltaCents: 1500, nestedGroupId: null, active: true, sortOrder: 0 },
      { id: 'mo-x2', groupId: 'mg-extras', label: 'Aguacate', priceDeltaCents: 2500, nestedGroupId: null, active: true, sortOrder: 1 },
    ],
    productIds: [],
  },
  {
    id: 'mg-termino',
    tenantId: 'tnt_demo',
    name: 'Término',
    minSelect: 1,
    maxSelect: 1,
    isRequired: true,
    active: true,
    createdAt: new Date('2026-02-01').toISOString(),
    updatedAt: new Date('2026-02-01').toISOString(),
    deletedAt: null,
    options: [
      { id: 'mo-t1', groupId: 'mg-termino', label: 'Rojo', priceDeltaCents: 0, nestedGroupId: null, active: true, sortOrder: 0 },
      { id: 'mo-t2', groupId: 'mg-termino', label: 'Medio', priceDeltaCents: 0, nestedGroupId: null, active: true, sortOrder: 1 },
      { id: 'mo-t3', groupId: 'mg-termino', label: 'Bien cocido', priceDeltaCents: 0, nestedGroupId: null, active: true, sortOrder: 2 },
    ],
    productIds: [],
  },
]
const stubProductLinks = new Map<string, Set<string>>() // groupId → set<productId>
const stubId = (p: string) => `${p}-${Math.random().toString(36).slice(2, 8)}`

function stubFindGroup(id: string): ModifierGroupDto {
  const g = stubGroups.find((x) => x.id === id)
  if (!g) throw new Error('Grupo no encontrado')
  return g
}

// ── GROUPS ────────────────────────────────────────────────────────────────

// Mapea el ModifierGroup del grafo (typed-document-node) al DTO de UI. Campos 1:1
// con el SDL de catalog-modifiers; normalizamos nullables.
interface GqlGroup {
  id: string; tenantId: string; name: string; minSelect: number; maxSelect: number
  isRequired: boolean; active: boolean; createdAt: string; updatedAt: string; deletedAt?: string | null
  productIds: ReadonlyArray<string>
  options: ReadonlyArray<{ id: string; groupId: string; label: string; priceDeltaCents: number; nestedGroupId?: string | null; active: boolean; sortOrder: number }>
}
function toUiGroup(g: GqlGroup): ModifierGroupDto {
  return {
    id: g.id, tenantId: g.tenantId, name: g.name,
    minSelect: g.minSelect, maxSelect: g.maxSelect, isRequired: g.isRequired,
    active: g.active, createdAt: g.createdAt, updatedAt: g.updatedAt, deletedAt: g.deletedAt ?? null,
    options: g.options.map((o) => ({
      id: o.id, groupId: o.groupId, label: o.label, priceDeltaCents: o.priceDeltaCents,
      nestedGroupId: o.nestedGroupId ?? null, active: o.active, sortOrder: o.sortOrder,
    })),
    productIds: [...g.productIds],
  }
}

interface GqlOption { id: string; groupId: string; label: string; priceDeltaCents: number; nestedGroupId?: string | null; active: boolean; sortOrder: number }
function toUiOption(o: GqlOption): ModifierOptionDto {
  return { id: o.id, groupId: o.groupId, label: o.label, priceDeltaCents: o.priceDeltaCents, nestedGroupId: o.nestedGroupId ?? null, active: o.active, sortOrder: o.sortOrder }
}

export async function listModifierGroups(
  params: ListModifierGroupsParams = {},
  signal?: AbortSignal,
): Promise<ModifierGroupListResponse> {
  if (PREVIEW_STUB) return mock({ items: stubGroups.filter((g) => g.deletedAt === null), total: stubGroups.length })
  // F1.3 / ADR-0009: lectura por el grafo (tenant del JWT, RLS). Las escrituras
  // (grupos/opciones/links) siguen REST hasta que el subgraph exponga mutations.
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(AdminModifierGroupsQuery, { limit: params.limit ?? 100 }, ctx).toPromise()
  if (res.error) throw res.error
  const g = res.data?.modifierGroups
  return { items: (g?.items ?? []).map(toUiGroup), total: g?.total ?? 0 }
}

export async function createModifierGroup(input: CreateGroupInput): Promise<ModifierGroupDto> {
  if (PREVIEW_STUB) {
    const g: ModifierGroupDto = {
      id: stubId('mg'),
      tenantId: authTenantId() || 'tnt_demo',
      name: input.name,
      minSelect: input.minSelect ?? 0,
      maxSelect: input.maxSelect ?? 1,
      isRequired: input.isRequired ?? false,
      active: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      deletedAt: null,
      options: [],
      productIds: [],
    }
    stubGroups.push(g)
    return mock(g)
  }
  // F9.4 / ADR-0009: alta por el grafo (tenant del JWT, RLS). Sin tenantId en el input.
  const res = await gqlClient.mutation(AdminCreateModifierGroupMutation, {
    input: {
      name: input.name,
      ...(input.minSelect !== undefined ? { minSelect: input.minSelect } : {}),
      ...(input.maxSelect !== undefined ? { maxSelect: input.maxSelect } : {}),
      ...(input.isRequired !== undefined ? { isRequired: input.isRequired } : {}),
    },
  }).toPromise()
  if (res.error) throw res.error
  const g = res.data?.createModifierGroup
  if (!g) throw new Error('createModifierGroup: empty response')
  return toUiGroup(g)
}

export async function updateModifierGroup(id: string, input: UpdateGroupInput): Promise<ModifierGroupDto> {
  if (PREVIEW_STUB) {
    const g = stubFindGroup(id)
    if (input.name !== undefined) g.name = input.name
    if (input.minSelect !== undefined) g.minSelect = input.minSelect
    if (input.maxSelect !== undefined) g.maxSelect = input.maxSelect
    if (input.isRequired !== undefined) g.isRequired = input.isRequired
    if (input.active !== undefined) g.active = input.active
    g.updatedAt = new Date().toISOString()
    return mock(g)
  }
  const res = await gqlClient.mutation(AdminUpdateModifierGroupMutation, {
    id,
    input: {
      ...(input.name !== undefined ? { name: input.name } : {}),
      ...(input.minSelect !== undefined ? { minSelect: input.minSelect } : {}),
      ...(input.maxSelect !== undefined ? { maxSelect: input.maxSelect } : {}),
      ...(input.isRequired !== undefined ? { isRequired: input.isRequired } : {}),
      ...(input.active !== undefined ? { active: input.active } : {}),
    },
  }).toPromise()
  if (res.error) throw res.error
  const g = res.data?.updateModifierGroup
  if (!g) throw new Error('updateModifierGroup: empty response')
  return toUiGroup(g)
}

export async function deleteModifierGroup(id: string): Promise<{ id: string }> {
  if (PREVIEW_STUB) {
    const i = stubGroups.findIndex((g) => g.id === id)
    if (i >= 0) stubGroups.splice(i, 1)
    return mock({ id })
  }
  const res = await gqlClient.mutation(AdminDeleteModifierGroupMutation, { id }).toPromise()
  if (res.error) throw res.error
  return { id }
}

// ── OPTIONS ────────────────────────────────────────────────────────────────

export async function addOption(groupId: string, input: CreateOptionInput): Promise<ModifierOptionDto> {
  if (PREVIEW_STUB) {
    const g = stubFindGroup(groupId)
    const o: ModifierOptionDto = {
      id: stubId('mo'),
      groupId,
      label: input.label,
      priceDeltaCents: input.priceDeltaCents ?? 0,
      nestedGroupId: null,
      active: true,
      sortOrder: input.sortOrder ?? g.options.length,
    }
    g.options.push(o)
    g.updatedAt = new Date().toISOString()
    return mock(o)
  }
  const res = await gqlClient.mutation(AdminAddModifierOptionMutation, {
    groupId,
    input: {
      label: input.label,
      ...(input.priceDeltaCents !== undefined ? { priceDeltaCents: input.priceDeltaCents } : {}),
      ...(input.sortOrder !== undefined ? { sortOrder: input.sortOrder } : {}),
    },
  }).toPromise()
  if (res.error) throw res.error
  const o = res.data?.addModifierOption
  if (!o) throw new Error('addOption: empty response')
  return toUiOption(o)
}

export async function updateOption(groupId: string, optionId: string, input: UpdateOptionInput): Promise<ModifierOptionDto> {
  if (PREVIEW_STUB) {
    const g = stubFindGroup(groupId)
    const o = g.options.find((x) => x.id === optionId)
    if (!o) throw new Error('Opción no encontrada')
    if (input.label !== undefined) o.label = input.label
    if (input.priceDeltaCents !== undefined) o.priceDeltaCents = input.priceDeltaCents
    if (input.sortOrder !== undefined) o.sortOrder = input.sortOrder
    if (input.active !== undefined) o.active = input.active
    return mock(o)
  }
  const res = await gqlClient.mutation(AdminUpdateModifierOptionMutation, {
    groupId,
    optionId,
    input: {
      ...(input.label !== undefined ? { label: input.label } : {}),
      ...(input.priceDeltaCents !== undefined ? { priceDeltaCents: input.priceDeltaCents } : {}),
      ...(input.sortOrder !== undefined ? { sortOrder: input.sortOrder } : {}),
      ...(input.active !== undefined ? { active: input.active } : {}),
    },
  }).toPromise()
  if (res.error) throw res.error
  const o = res.data?.updateModifierOption
  if (!o) throw new Error('updateOption: empty response')
  return toUiOption(o)
}

export async function removeOption(groupId: string, optionId: string): Promise<{ id: string }> {
  if (PREVIEW_STUB) {
    const g = stubFindGroup(groupId)
    g.options = g.options.filter((x) => x.id !== optionId)
    return mock({ id: optionId })
  }
  const res = await gqlClient.mutation(AdminRemoveModifierOptionMutation, { groupId, optionId }).toPromise()
  if (res.error) throw res.error
  return { id: optionId }
}

// ── PRODUCT LINKS ──────────────────────────────────────────────────────────

export async function linkProduct(
  groupId: string,
  productId: string,
  body: { sortOrder?: number } = {},
): Promise<{ groupId: string; productId: string }> {
  if (PREVIEW_STUB) {
    let set = stubProductLinks.get(groupId)
    if (!set) { set = new Set(); stubProductLinks.set(groupId, set) }
    set.add(productId)
    return mock({ groupId, productId })
  }
  const res = await gqlClient.mutation(AdminLinkModifierGroupToProductMutation, {
    groupId,
    productId,
    ...(body.sortOrder !== undefined ? { sortOrder: body.sortOrder } : {}),
  }).toPromise()
  if (res.error) throw res.error
  return { groupId, productId }
}

export async function unlinkProduct(
  groupId: string,
  productId: string,
): Promise<{ groupId: string; productId: string }> {
  if (PREVIEW_STUB) {
    stubProductLinks.get(groupId)?.delete(productId)
    return mock({ groupId, productId })
  }
  const res = await gqlClient.mutation(AdminUnlinkModifierGroupFromProductMutation, { groupId, productId }).toPromise()
  if (res.error) throw res.error
  return { groupId, productId }
}

/**
 * Best-effort fetch of the productIds linked to a group. The list endpoint does
 * not include links, and a GET-links route is not in the contract — the closest
 * thing today is to query the group list filtered by productId for each candidate
 * product, which is O(N) and not scalable. We therefore expose a stub-friendly
 * helper that returns the productIds we've tracked locally; the admin UI uses
 * it as a starting hint and reconciles by re-POSTing/DELETEing on save.
 */
export function getLinkedProductIdsStub(groupId: string): string[] {
  return [...(stubProductLinks.get(groupId) ?? [])]
}
